import jsPDF from "jspdf";
import { KAFFA_LOGO_URL } from "@/components/brand/KaffaLogo";

/**
 * Generates a payment receipt PDF for a tour booking.
 *
 * The layout uses a header strip with the Kaffa logo, the order number,
 * a left column with the booking details, a right column with the payment
 * info, a totals line, and a clear "DEMO PAYMENT" disclaimer at the bottom.
 */
export async function generateReceiptPdf({ receipt, tour, t, locale = "pt-BR" }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  // ----- Brand bar -----
  doc.setFillColor(94, 31, 12); // #5e1f0c
  doc.rect(0, 0, W, 88, "F");

  // Try to embed logo (skips silently if it can't fetch — CORS, etc.)
  try {
    const imgData = await loadImageDataUrl(KAFFA_LOGO_URL);
    if (imgData) doc.addImage(imgData, "PNG", margin, 20, 48, 48);
  } catch { /* ignore */ }

  doc.setTextColor(252, 251, 250);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("KAFFA EXCHANGE", margin + 60, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("B2B Coffee Trading · Origin Tours", margin + 60, 58);
  doc.setFontSize(8);
  doc.text("kaffaex.com · trade@kaffaexchange.com", margin + 60, 72);

  // Order number top-right
  doc.setFontSize(9);
  doc.text((t ? t("receipt.title") : "Receipt").toUpperCase(), W - margin, 42, { align: "right" });
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(receipt.order_number, W - margin, 62, { align: "right" });

  y = 120;
  doc.setTextColor(0, 0, 0);

  // ----- PAID stamp -----
  const stampY = y;
  doc.setDrawColor(16, 185, 129); // emerald-500
  doc.setFillColor(220, 252, 231); // emerald-100
  doc.roundedRect(margin, stampY, 90, 22, 3, 3, "FD");
  doc.setTextColor(5, 122, 85);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text((t ? t("receipt.paid_stamp") : "PAID").toUpperCase(), margin + 45, stampY + 14, { align: "center" });
  y += 40;

  // ----- Customer block -----
  doc.setTextColor(120, 120, 120);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("CUSTOMER", margin, y);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(receipt.user_company || receipt.user_email || "—", margin, y + 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(receipt.user_email || "", margin, y + 32);

  // Right column: paid at + card
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(8);
  doc.text("PAID AT", W - margin, y, { align: "right" });
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  const paidAt = receipt.paid_at
    ? new Date(receipt.paid_at).toLocaleString(locale === "pt-BR" ? "pt-BR" : "en-US")
    : "—";
  doc.text(paidAt, W - margin, y + 16, { align: "right" });
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(8);
  doc.text("PAYMENT METHOD", W - margin, y + 32, { align: "right" });
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(`${receipt.card_brand || "Card"} **** ${receipt.card_last4 || ""}`, W - margin, y + 48, { align: "right" });

  y += 80;

  // ----- Order details -----
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, W - margin, y);
  y += 18;
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(8);
  doc.text("ORDER DETAILS", margin, y);
  y += 8;

  const rows = [
    [t ? t("receipt.tour") : "Tour", receipt.tour_name || (tour && tour.name) || "—"],
    [t ? t("receipt.date") : "Date", receipt.selected_date || "—"],
    [t ? t("receipt.passengers") : "Passengers", `${receipt.passengers} pax · ${receipt.duration_days} days`],
    [t ? t("receipt.airport") : "Airport", receipt.origin_airport || "—"],
  ];

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  for (const [k, v] of rows) {
    y += 18;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(String(k).toUpperCase(), margin, y);
    doc.setTextColor(0, 0, 0);
    const wrapped = doc.splitTextToSize(String(v), W - 2 * margin - 160);
    doc.text(wrapped, margin + 160, y);
    if (wrapped.length > 1) y += (wrapped.length - 1) * 12;
  }

  y += 30;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, W - margin, y);
  y += 24;

  // ----- Total -----
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text((t ? t("receipt.total_paid") : "Total paid").toUpperCase(), margin, y);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(200, 117, 32); // amber accent
  doc.text(`USD ${Number(receipt.total_price_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`, W - margin, y + 6, { align: "right" });

  y += 60;

  // ----- Disclaimer -----
  doc.setFont("helvetica", "italic");
  doc.setTextColor(150, 150, 150);
  doc.setFontSize(8);
  const disclaimer = (t ? t("receipt.admin_will_contact") : "Our team will reach out within 24h to confirm next steps.");
  const wrapped = doc.splitTextToSize(disclaimer, W - 2 * margin);
  doc.text(wrapped, margin, y);
  y += wrapped.length * 11;

  if (receipt.demo_payment) {
    y += 14;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(180, 100, 0);
    doc.text("⚠ DEMO PAYMENT — NO REAL CHARGE WAS MADE.", margin, y);
  }

  // ----- Footer -----
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(8);
  doc.text("Kaffa Exchange · São Paulo · Brasil · trade@kaffaexchange.com", margin, doc.internal.pageSize.getHeight() - 24);
  doc.text(receipt.order_number, W - margin, doc.internal.pageSize.getHeight() - 24, { align: "right" });

  doc.save(`Kaffa-Receipt-${receipt.order_number}.pdf`);
}

// Load an image URL into a base64 data URL so jsPDF can embed it.
async function loadImageDataUrl(url) {
  if (!url) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
